// EventDetail.jsx
import React, {
  useEffect,
  useState,
  useCallback,
  useMemo,
  useRef,
  memo,
} from "react";
import { useParams, Link, useSearchParams } from "react-router-dom";
import {
  CalendarPlus,
  CreditCard,
  Globe2,
  Users,
  ArrowRight,
  Mic2,
  CheckCircle2,
} from "lucide-react";
import { toast } from "sonner";
import TableCard from "@/components/TableCard/TableCard";
import BrandLogo from "@/components/BrandLogo/BrandLogo";
import WelcomeVideoOverlay from "@/components/WelcomeVideoOverlay/WelcomeVideoOverlay";
import { useAuth } from "@/context/AuthContext";
import { getEventById, bookEvent } from "@/Services/EventServices";
import {
  getSessionTables,
  joinSessionTable,
} from "@/Services/EventMeetingServices";

// ---------------------------------------------------------------------------
// PURE UTILITY FUNCTIONS — module-scope, zero allocation per render
// ---------------------------------------------------------------------------

/**
 * Parses "(GMT-4)" / "(GMT+5:30)" → total offset in minutes, or null.
 */
const gmtLabelToOffsetMinutes = (tz) => {
  const s = String(tz || "")
    .replace(/[()]/g, "")
    .trim();
  const m = s.match(/GMT\s*([+-])\s*(\d{1,2})(?::(\d{2}))?/i);
  if (!m) return null;
  const sign = m[1] === "-" ? -1 : 1;
  return sign * (Number(m[2] || 0) * 60 + Number(m[3] || 0));
};

/**
 * Builds a full ISO-8601 string from separate API fields.
 */
const isoFromParts = (dateStr, timeStr, tzLabel) => {
  if (!dateStr || !timeStr) return "";
  const mins = gmtLabelToOffsetMinutes(tzLabel);
  if (mins == null) return `${dateStr}T${timeStr}:00Z`;
  const sign = mins < 0 ? "-" : "+";
  const abs = Math.abs(mins);
  const hh = String(Math.floor(abs / 60)).padStart(2, "0");
  const mm = String(abs % 60).padStart(2, "0");
  return `${dateStr}T${timeStr}:00${sign}${hh}:${mm}`;
};

/**
 * Adds minutes to an "HH:MM" string and returns a new "HH:MM" string.
 */
const addMinutesToHHMM = (hhmm, minsToAdd) => {
  const m = String(hhmm || "").match(/^(\d{1,2}):(\d{2})$/);
  if (!m) return "";
  const total = Number(m[1]) * 60 + Number(m[2]) + minsToAdd;
  const safe = ((total % 1440) + 1440) % 1440;
  return `${String(Math.floor(safe / 60)).padStart(2, "0")}:${String(safe % 60).padStart(2, "0")}`;
};

/**
 * Strips HTML tags and collapses whitespace.
 */
const stripHtml = (html) =>
  html
    ? String(html)
        .replace(/<[^>]*>/g, " ")
        .replace(/\s+/g, " ")
        .trim()
    : "";

/**
 * Converts any date-like value to a safe ISO string.
 * Falls back to `fallbackMs` when the value is invalid.
 */
const safeIso = (dateLike, fallbackMs = Date.now()) => {
  const d = new Date(dateLike);
  return Number.isFinite(d.getTime())
    ? d.toISOString()
    : new Date(fallbackMs).toISOString();
};

// ---------------------------------------------------------------------------
// FORMATTING HELPERS — pure, module-scope
// ---------------------------------------------------------------------------

const fmtTime = (iso, tz) => {
  try {
    if (!iso) return "";
    const d = new Date(iso);
    if (!Number.isFinite(d.getTime())) return "";
    const offsetMins = gmtLabelToOffsetMinutes(tz);
    if (offsetMins != null) {
      return new Date(d.getTime() + offsetMins * 60_000).toLocaleTimeString(
        undefined,
        { hour: "numeric", minute: "2-digit", timeZone: "UTC" },
      );
    }
    return d.toLocaleTimeString(undefined, {
      hour: "numeric",
      minute: "2-digit",
      timeZone: tz || undefined,
    });
  } catch {
    return "";
  }
};

const fmtDate = (iso, tz) => {
  try {
    if (!iso) return "";
    const d = new Date(iso);
    if (!Number.isFinite(d.getTime())) return "";
    const offsetMins = gmtLabelToOffsetMinutes(tz);
    const opts = {
      weekday: "long",
      month: "long",
      day: "numeric",
      year: "numeric",
    };
    if (offsetMins != null) {
      return new Date(d.getTime() + offsetMins * 60_000).toLocaleDateString(
        undefined,
        { ...opts, timeZone: "UTC" },
      );
    }
    return d.toLocaleDateString(undefined, {
      ...opts,
      timeZone: tz || undefined,
    });
  } catch {
    return "";
  }
};

const liveLabel = (start, end) => {
  const now = Date.now();
  const s = new Date(start).getTime();
  const e = new Date(end).getTime();
  if (!Number.isFinite(s) || !Number.isFinite(e)) return "TBA";
  if (now < s) {
    const diff = s - now;
    const days = Math.floor(diff / 86_400_000);
    if (days >= 1) return `Starts in ${days}d`;
    const hours = Math.floor(diff / 3_600_000);
    return hours >= 1 ? `Starts in ${hours}h` : "Starts soon";
  }
  return now < e ? "Live now" : "Ended";
};

// ---------------------------------------------------------------------------
// ICS CALENDAR HELPERS — memoised at call-site via useMemo
// ---------------------------------------------------------------------------

const icsDateStamp = (isoStr) =>
  isoStr.replace(/[-:]/g, "").replace(".000", "").replace("Z", "Z");

const buildIcsContent = (uid, startIso, endIso, summary, description) =>
  [
    "BEGIN:VCALENDAR",
    "VERSION:2.0",
    "PRODID:-//vmeetio//static//EN",
    "CALSCALE:GREGORIAN",
    "BEGIN:VEVENT",
    `UID:${uid}`,
    `DTSTAMP:${icsDateStamp(new Date().toISOString())}`,
    `DTSTART:${icsDateStamp(startIso)}`,
    `DTEND:${icsDateStamp(endIso)}`,
    `SUMMARY:${String(summary || "").replace(/\n/g, " ")}`,
    `DESCRIPTION:${String(description || "").replace(/\n/g, "\\n")}`,
    "END:VEVENT",
    "END:VCALENDAR",
  ].join("\r\n") + "\r\n";

const icsDataUrlForEvent = (e) => {
  const startIso = safeIso(e.start_time, Date.now());
  const endIso = safeIso(e.end_time, Date.now() + 3_600_000);
  return `data:text/calendar;charset=utf-8,${encodeURIComponent(
    buildIcsContent(
      `vmeetio-event-${e.id}@local`,
      startIso,
      endIso,
      e.title,
      e.description,
    ),
  )}`;
};

const icsDataUrlForSession = (s, eventTitle) => {
  const startIso = safeIso(s.start_time, Date.now());
  const endIso = safeIso(s.end_time, Date.now() + 3_600_000);
  return `data:text/calendar;charset=utf-8,${encodeURIComponent(
    buildIcsContent(
      `vmeetio-session-${s.id}@local`,
      startIso,
      endIso,
      `${s.title || ""} · ${eventTitle || ""}`,
      s.description,
    ),
  )}`;
};

// ---------------------------------------------------------------------------
// DATA NORMALIZERS — pure, module-scope
// ---------------------------------------------------------------------------

const normalizeTablesFromSessionTablesApi = (tables, eventId) =>
  (Array.isArray(tables) ? tables : []).map((t) => ({
    id: t._id,
    event_id: eventId,
    circle: t?.sponsorDetails?.title || t?.sponcer?.title || "",
    topic: (t.tableName || `Table ${t.tableNumber || ""}`).trim(),
    description: stripHtml(t.agenda || ""),
    seats: Number(t.capacity ?? 8),
    taken: Number(t.currentOccupancy ?? 0),
    is_live: !!t.isActive,
    moderator: "",
    moderator_avatar: "",
    attendees: [],
    waitlist: [],
    welcome_video_url:
      t?.sponsorDetails?.introVideo || t?.sponcer?.introVideo || "",
    __raw: t,
  }));

const normalizeEventResponse = (d) => {
  const start_time = isoFromParts(d.startDate, d.startTime, d.timezone);
  const end_time = isoFromParts(
    d.endDate || d.startDate,
    d.endTime,
    d.timezone,
  );

  const sponsors = (d.sponcers || []).map((sp) => ({
    id: sp._id,
    scope: "event",
    event_id: d._id,
    company: sp.title,
    logo: sp.image,
    status: "approved",
    value_prop: stripHtml(sp.descp),
    website: sp.website || "",
    // ✅ Add these fields to match BrandDetail expectations
    contact_email: sp.email || "",
    phone: sp.phone || "",
    linkedin: sp.linkedin || "",
    presentation_url: sp.presentationUrl || "",
    pdf_url: sp.pdfUrl || "",
    pdf_caption: sp.pdfCaption || "Download one-pager (PDF)",
    video_url: sp.introVideo || "",
    bgColor: sp.bgcColor || "#1E293B",
    overview: stripHtml(sp.descp) || "",
    chapterName: sp.chapterName,
    visibility: sp.visibility,
  }));

  const sessions = (d.sessions || []).map((s) => {
    const tz = s.timezone || d.timezone;
    const endTime = s.endTime || addMinutesToHHMM(s.startTime, 60);
    return {
      id: s._id,
      event_id: d._id,
      title: s.name,
      description: stripHtml(s.description),
      start_time: isoFromParts(s.date, s.startTime, tz),
      end_time: isoFromParts(s.date, endTime, tz),
      speaker: "",
      timezone: tz,
      meetingStatus: s.meetingStatus,
    };
  });

  const event = {
    id: d._id,
    title: d.eventName,
    description: stripHtml(d.description),
    start_time,
    end_time,
    timezone: d.timezone || "UTC",
    cover: d.eventImage,
    tags: [d.entry, d.meetingType, d.visibility].filter(Boolean),
    registered: Number(d.bookingCount ?? d.bookingDetails?.length ?? 0),
    tables_count: 0,
    price: Number(d.ticketPrice ?? 0),
    welcome_video_url: "",
    featured_moderator:
      Array.isArray(d.hosts) &&
      d.hosts.length > 0 &&
      typeof d.hosts[0] === "object"
        ? {
            name: d.hosts[0]?.name || "Host",
            title: d.hosts[0]?.experience || "",
            avatar: d.hosts[0]?.image || "",
          }
        : null,
  };

  // Determine active session: live first, then first
  const liveSession = d.sessions?.find((s) => s.meetingStatus === "live");
  const activeSessionId = liveSession?._id || d.sessions?.[0]?._id || null;

  return { event, sponsors, sessions, activeSessionId, raw: d };
};

// ---------------------------------------------------------------------------
// MODAL — shared backdrop + panel primitives (avoids per-modal boilerplate)
// ---------------------------------------------------------------------------

/**
 * Shared modal shell — handles backdrop click, focus trap not included
 * (add if accessibility requires it).
 */
const ModalShell = memo(({ onClose, testId, children }) => (
  <div
    className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
    onClick={onClose}
    data-testid={testId}
  >
    <div
      className="w-[min(96vw,520px)] rounded-3xl border border-white/15 bg-[#0A1D1A] p-7 shadow-2xl"
      onClick={(e) => e.stopPropagation()}
    >
      {children}
    </div>
  </div>
));
ModalShell.displayName = "ModalShell";

// ---------------------------------------------------------------------------
// REGISTER MODAL
// ---------------------------------------------------------------------------

const RegisterModal = memo(({ event, open, onClose, user, onBooked }) => {
  const [agree, setAgree] = useState(false);
  const [busy, setBusy] = useState(false);

  // Reset checkbox every time the modal opens
  useEffect(() => {
    if (open) setAgree(false);
  }, [open]);

  const submit = useCallback(async () => {
    if (!agree) {
      toast.error("Please agree to the Terms & Conditions and Privacy Policy.");
      return;
    }
    if (!user || user === false) {
      toast.error("Please sign in to register.");
      return;
    }
    setBusy(true);
    try {
      const res = await bookEvent({
        newsId: event.id,
        agreeTerms: true,
        userName: user?.name,
        firstName: user?.name?.split(" ")?.[0],
        lastName: user?.name?.split(" ")?.slice(1)?.join(" "),
        email: user?.email,
      });

      const ticketNumber = res?.booking?.ticketNumber || "";
      if (!ticketNumber && !/booked/i.test(String(res?.message || ""))) {
        toast.error(res?.message || "Could not register.");
        return;
      }
      toast.success(res?.message || "Event booked successfully");
      onClose?.();
      await onBooked?.(ticketNumber);
    } catch {
      toast.error("Could not register.");
    } finally {
      setBusy(false);
    }
  }, [agree, user, event, onClose, onBooked]);

  // Don't mount DOM at all when closed
  if (!open) return null;

  return (
    <ModalShell onClose={onClose} testId="event-register-modal">
      <p className="overline mb-2">Register</p>
      <h3 className="font-display text-2xl tracking-tight mb-2">
        {event?.title}
      </h3>

      <label className="flex items-start gap-3 mt-4 cursor-pointer">
        <input
          type="checkbox"
          checked={agree}
          onChange={(e) => setAgree(e.target.checked)}
          className="mt-1"
        />
        <span className="text-sm text-white/70">
          I agree to the{" "}
          <a
            href="/terms"
            target="_blank"
            rel="noreferrer"
            className="underline"
          >
            Terms & Conditions
          </a>{" "}
          and{" "}
          <a
            href="/privacy"
            target="_blank"
            rel="noreferrer"
            className="underline"
          >
            Privacy Policy
          </a>
          .
        </span>
      </label>

      <div className="flex gap-3 mt-6">
        <button
          onClick={submit}
          className="btn-gold flex-1 justify-center"
          data-testid="event-register-confirm"
          type="button"
          disabled={!agree || busy}
        >
          {busy ? "Registering…" : "Confirm registration"}{" "}
          <ArrowRight size={16} />
        </button>
        <button
          onClick={onClose}
          className="btn-ghost"
          type="button"
          disabled={busy}
        >
          Cancel
        </button>
      </div>
    </ModalShell>
  );
});
RegisterModal.displayName = "RegisterModal";

// ---------------------------------------------------------------------------
// CANCEL REGISTRATION MODAL
// ---------------------------------------------------------------------------

const CancelRegistrationModal = memo(
  ({ open, onClose, eventId, onCancelled }) => {
    const [busy, setBusy] = useState(false);

    const submit = useCallback(async () => {
      setBusy(true);
      try {
        const res = await bookEvent({ newsId: eventId, cancel: true });
        if (
          res?.removed === true ||
          /cancel/i.test(String(res?.message || ""))
        ) {
          toast.success(res?.message || "Booking cancelled successfully");
          onClose?.();
          await onCancelled?.();
          return;
        }
        toast.error(res?.message || "Could not cancel registration.");
      } catch {
        toast.error("Could not cancel registration.");
      } finally {
        setBusy(false);
      }
    }, [eventId, onClose, onCancelled]);

    if (!open) return null;

    return (
      <ModalShell onClose={onClose} testId="cancel-register-modal">
        <p className="overline mb-2">Cancel registration</p>
        <h3 className="font-display text-2xl tracking-tight mb-2">
          Are you sure?
        </h3>
        <p className="text-sm text-white/65 mb-6">
          Your booking will be removed.
        </p>
        <div className="flex gap-3">
          <button
            onClick={submit}
            className="btn-gold flex-1 justify-center"
            type="button"
            disabled={busy}
          >
            {busy ? "Canceling…" : "Yes, cancel"} <ArrowRight size={16} />
          </button>
          <button
            onClick={onClose}
            className="btn-ghost"
            type="button"
            disabled={busy}
          >
            Keep my seat
          </button>
        </div>
      </ModalShell>
    );
  },
);
CancelRegistrationModal.displayName = "CancelRegistrationModal";

// ---------------------------------------------------------------------------
// PRE-JOIN MODAL
// ---------------------------------------------------------------------------

const StaticPreJoinModal = memo(({ open, onClose, onReady }) => {
  if (!open) return null;
  return (
    <ModalShell onClose={onClose} testId="prejoin-modal">
      <p className="overline mb-2">Pre-join check</p>
      <h3 className="font-display text-2xl tracking-tight mb-2">
        You're about to enter the room
      </h3>
      <p className="text-sm text-white/65 mb-6">
        Static demo. In production, this checks mic/camera and confirms your
        setup.
      </p>
      <div className="flex gap-3">
        <button
          onClick={onReady}
          className="btn-gold flex-1 justify-center"
          data-testid="prejoin-ready"
          type="button"
        >
          Enter now <ArrowRight size={16} />
        </button>
        <button
          onClick={onClose}
          className="btn-ghost"
          data-testid="prejoin-close"
          type="button"
        >
          Close
        </button>
      </div>
    </ModalShell>
  );
});
StaticPreJoinModal.displayName = "StaticPreJoinModal";

// Offer modal — null stub, preserved for API compatibility
const StaticWaitlistOfferModal = memo(({ offer }) => {
  if (!offer) return null;
  return null;
});
StaticWaitlistOfferModal.displayName = "StaticWaitlistOfferModal";

// ---------------------------------------------------------------------------
// SESSION ROW — memoised; only re-renders when its own props change
// ---------------------------------------------------------------------------

const SessionRow = memo(function SessionRow({
  s,
  i,
  eventTitle,
  isRegistered,
  isMyRsvp,
  onToggle,
  tablesCount = 0,
  lobbyTo = "#",
}) {
  const [busy, setBusy] = useState(false);

  // Stable ICS URL — recomputed only when session id or eventTitle changes
  const icsUrl = useMemo(
    () => icsDataUrlForSession(s, eventTitle),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [s.id, s.start_time, s.end_time, eventTitle],
  );

  const toggle = useCallback(async () => {
    if (!isRegistered) {
      toast.info("Register for the event first to RSVP for sessions.");
      return;
    }
    setBusy(true);
    try {
      onToggle?.();
    } finally {
      setBusy(false);
    }
  }, [isRegistered, onToggle]);

  return (
    <li
      data-testid={`session-${s.id}`}
      className={`glass rounded-2xl p-6 md:p-8 transition-all duration-300 hover:-translate-y-1 hover:border-white/20 ${
        isMyRsvp ? "border-[#7BC8A6]/35" : ""
      }`}
    >
      <div className="grid md:grid-cols-12 gap-6 items-start">
        {/* LEFT */}
        <div className="md:col-span-4">
          <p className="overline mb-2">
            Session {String(i + 1).padStart(2, "0")}
          </p>
          <p className="text-sm font-mono-acc text-[#C6A87C]">
            {fmtTime(s.start_time, s.timezone)} –{" "}
            {fmtTime(s.end_time, s.timezone)}
          </p>

          <div className="mt-4 inline-flex overflow-hidden rounded-xl border border-white/10 bg-white/[0.03]">
            <div className="px-4 py-2 flex flex-col items-center justify-center min-w-[84px]">
              <div className="font-display text-3xl leading-none text-white">
                {tablesCount}
              </div>
              <div className="overline text-white/60 mt-1">Tables</div>
            </div>
          </div>

          {s.speaker && (
            <p className="text-xs text-white/55 mt-3 flex items-center gap-1.5">
              <Mic2 size={11} />
              {s.speaker}
            </p>
          )}
        </div>

        {/* RIGHT */}
        <div className="md:col-span-8">
          <h3 className="font-display text-2xl tracking-tight mb-2">
            {s.title}
          </h3>
          <p className="text-sm text-white/65 leading-relaxed">
            {s.description}
          </p>

          <div className="mt-5 flex items-center gap-3 flex-wrap">
            <button
              onClick={toggle}
              disabled={busy}
              data-testid={`session-rsvp-${s.id}`}
              className={`text-xs px-3 py-1.5 rounded-full inline-flex items-center gap-1.5 transition-colors disabled:opacity-50 ${
                isMyRsvp
                  ? "bg-[#7BC8A6]/15 border border-[#7BC8A6]/45 text-[#7BC8A6] hover:bg-[#7BC8A6]/25"
                  : "bg-[#C6A87C]/10 border border-[#C6A87C]/35 text-[#C6A87C] hover:bg-[#C6A87C]/20"
              }`}
              type="button"
            >
              {isMyRsvp ? (
                <>
                  <CheckCircle2 size={11} /> You're in
                </>
              ) : (
                "RSVP to this session"
              )}
            </button>

            <a
              href={icsUrl}
              download={`vmeetio-session-${s.id}.ics`}
              data-testid={`session-ics-${s.id}`}
              className="text-xs text-white/65 hover:text-[#C6A87C] inline-flex items-center gap-1"
            >
              <CalendarPlus size={11} /> Add to calendar
            </a>

            <Link
              to={lobbyTo}
              className="text-xs text-white/65 hover:text-[#C6A87C] inline-flex items-center gap-1"
              data-testid={`session-lobby-${s.id}`}
            >
              Enter the lobby <ArrowRight size={11} />
            </Link>
          </div>
        </div>
      </div>
    </li>
  );
});

// ---------------------------------------------------------------------------
// HERO SECTION — memoised so registration state changes don't repaint it
// ---------------------------------------------------------------------------

const EventHero = memo(function EventHero({
  event,
  isLive,
  myReg,
  eventSponsors,
  icsUrl,
  onRegisterOpen,
  onCancelOpen,
  onPreJoinOpen,
  onCompletePayment,
}) {
  const mainSponsor = eventSponsors[0] || null;
  const otherEventSponsors = eventSponsors.slice(1);

  return (
    <section
      className="relative overflow-hidden"
      data-testid="event-detail-hero"
    >
      <div className="absolute inset-0 -z-10">
        <img
          src={event.cover}
          alt=""
          loading="eager"
          fetchPriority="high"
          decoding="async"
          className="w-full h-full object-cover opacity-25"
        />
        <div
          className="absolute inset-0"
          style={{
            background:
              "linear-gradient(180deg, rgba(6,20,18,0.6) 0%, #061412 100%)",
          }}
        />
      </div>

      <div className="container-x pt-24 md:pt-32 pb-16 md:pb-20">
        <Link
          to="/events"
          className="overline text-white/55 hover:text-white inline-flex items-center gap-2 mb-8"
          data-testid="back-to-events"
        >
          ← Back to calendar
        </Link>

        <p className="overline mb-5">
          {liveLabel(event.start_time, event.end_time)} ·{" "}
          {(event.tags || []).join(" · ")}
        </p>

        <h1 className="font-display font-light text-5xl md:text-7xl tracking-tighter leading-[0.95] mb-8 max-w-4xl">
          {event.title}
        </h1>

        <p className="text-white/70 text-lg max-w-2xl leading-relaxed mb-10">
          {event.description}
        </p>

        {/* Meta grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-8 max-w-3xl">
          <div>
            <p className="overline mb-2">When</p>
            <p className="text-sm">
              {fmtDate(event.start_time, event.timezone)}
            </p>
            <p className="text-sm text-white/60 mt-1">
              {fmtTime(event.start_time, event.timezone)} –{" "}
              {fmtTime(event.end_time, event.timezone)}{" "}
              {String(event.timezone || "UTC").replace(/[()]/g, "")}
            </p>
          </div>
          <div>
            <p className="overline mb-2">Format</p>
            <p className="text-sm flex items-center gap-2">
              <Globe2 size={14} className="text-[#C6A87C]" /> Virtual
            </p>
          </div>
          <div>
            <p className="overline mb-2">Registered</p>
            <p className="text-sm flex items-center gap-2">
              <Users size={14} className="text-[#C6A87C]" /> {event.registered}
            </p>
          </div>
          <div>
            <p className="overline mb-2">Tables</p>
            <p className="text-sm">
              {event.tables_count} parallel conversations
            </p>
          </div>
        </div>

        {/* Main sponsor */}
        {mainSponsor && (
          <Link
            to={`/brands/${encodeURIComponent(mainSponsor.company)}`}
            state={{ sponsorData: mainSponsor }} // ✅ Add this
            className="mt-10 group flex items-center gap-5 rounded-2xl border border-[#C6A87C]/35 bg-gradient-to-r from-[#C6A87C]/10 to-transparent px-6 py-5 max-w-2xl transition-all hover:border-[#C6A87C]/55 hover:bg-[#C6A87C]/15"
            data-testid={`main-sponsor-${mainSponsor.company}`}
          >
            <BrandLogo
              company={mainSponsor.company}
              logo={mainSponsor.logo}
              size={64}
            />
            <div className="min-w-0">
              <p className="text-[10px] uppercase tracking-[0.22em] text-[#C6A87C] mb-1.5">
                Presented by
              </p>
              <p className="font-display text-xl md:text-2xl tracking-tight leading-tight">
                {mainSponsor.company}
              </p>
              {mainSponsor.value_prop && (
                <p className="text-xs text-white/55 mt-1 line-clamp-1 max-w-md">
                  {mainSponsor.value_prop}
                </p>
              )}
            </div>
            <ArrowRight
              size={16}
              className="text-[#C6A87C] ml-auto opacity-0 group-hover:opacity-100 transition-opacity"
            />
          </Link>
        )}

        {/* Other sponsors */}
        {otherEventSponsors.length > 0 && (
          <div
            className="mt-6 flex flex-wrap items-center gap-3"
            data-testid="detail-event-sponsors"
          >
            <span className="overline">Also supported by</span>
            {otherEventSponsors.slice(0, 5).map((s) => (
              <Link
                key={s.id}
                to={`/brands/${encodeURIComponent(s.company)}`}
                state={{ sponsorData: s }} // ✅ Add this
                className="text-xs px-3 py-1.5 rounded-full bg-[#C6A87C]/10 border border-[#C6A87C]/30 text-[#C6A87C] hover:bg-[#C6A87C]/20 flex items-center gap-2"
                data-testid={`detail-sponsor-${s.company}`}
              >
                <BrandLogo company={s.company} logo={s.logo} size={18} />
                {s.company}
              </Link>
            ))}
          </div>
        )}

        {/* CTA bar */}
        <div
          className="mt-10 flex gap-3 flex-wrap items-center"
          data-testid="event-detail-cta-bar"
        >
          {/* {isLive && myReg && (
            <button
              onClick={onPreJoinOpen}
              data-testid="event-detail-enter-room"
              className="btn-gold inline-flex items-center gap-2"
            >
              <span className="w-2 h-2 rounded-full bg-[#7BC8A6] ring-live mr-1" />
              Enter the room <ArrowRight size={16} />
            </button>
          )} */}

          {myReg ? (
            <>
              <span
                data-testid="event-detail-registered-pill"
                className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full bg-[#7BC8A6]/15 border border-[#7BC8A6]/45 text-[#7BC8A6] text-sm"
              >
                <CheckCircle2 size={15} /> You're in
                {myReg.ref_code && (
                  <span
                    className="ml-2 font-mono text-[11px] tracking-wider text-[#C6A87C]"
                    data-testid="event-detail-ref"
                  >
                    {myReg.ref_code}
                  </span>
                )}
              </span>

              <button
                type="button"
                className="btn-ghost text-sm"
                onClick={onCancelOpen}
                data-testid="event-detail-cancel-cta"
              >
                Cancel registration
              </button>

              {/* {myReg.payment_status === "pending" &&
              Number(event.price || 0) > 0 ? (
                <button
                  onClick={onCompletePayment}
                  data-testid="event-detail-pay-cta"
                  className="btn-gold"
                  type="button"
                >
                  <CreditCard size={14} /> Complete payment — $
                  {Math.round(event.price)}
                </button>
              ) : (
                <button
                  type="button"
                  onClick={() => {
                    document
                      .querySelector('[data-testid="event-tables-section"]')
                      ?.scrollIntoView({ behavior: "smooth", block: "start" });
                  }}
                  data-testid="event-detail-open-cta"
                  className="btn-gold"
                >
                  Open your seat <ArrowRight size={16} />
                </button>
              )} */}

              <a
                href={icsUrl}
                download={`vmeetio-${event.id}.ics`}
                data-testid="event-detail-ics"
                className="btn-ghost text-sm"
              >
                <CalendarPlus size={14} /> Add to calendar
              </a>
            </>
          ) : (
            <>
              <button
                type="button"
                onClick={onRegisterOpen}
                data-testid="event-detail-register-cta"
                className="btn-gold"
              >
                {Number(event.price || 0) > 0
                  ? `Register — $${Math.round(event.price)}`
                  : "Register to attend"}{" "}
                <ArrowRight size={16} />
              </button>

              <a
                href={icsUrl}
                download={`vmeetio-${event.id}.ics`}
                data-testid="event-detail-ics"
                className="btn-ghost text-sm"
              >
                <CalendarPlus size={14} /> Add to calendar
              </a>
            </>
          )}

          <Link
            to={`/sponsor?event=${event.id}`}
            className="btn-ghost text-sm"
            data-testid="event-detail-sponsor-cta"
          >
            Sponsor this event
          </Link>
        </div>
      </div>
    </section>
  );
});

// ---------------------------------------------------------------------------
// SESSIONS SECTION — memoised
// ---------------------------------------------------------------------------

const SessionsSection = memo(function SessionsSection({
  sessions,
  event,
  myReg,
  mySessionRsvps,
  tablesCountBySession,
  onToggleRsvp,
}) {
  return (
    <section className="section" data-testid="agenda-section">
      <div className="container-x">
        <div className="grid md:grid-cols-12 gap-12 mb-12">
          <div className="md:col-span-5">
            <p className="overline mb-4">Master sessions · pre-set agenda</p>
            <h2 className="font-display font-light text-4xl md:text-5xl tracking-tight leading-tight">
              Focus on the topics that
              <br />
              <span className="italic text-[#C6A87C]">
                move you the fastest.
              </span>
            </h2>
          </div>
          <div className="md:col-span-6 md:col-start-7">
            <p className="text-white/65 leading-relaxed">
              Each event is broken into master sessions — short, focused blocks.
              Pick the ones that matter to you and skip the rest.
            </p>
          </div>
        </div>

        <ol className="space-y-3" data-testid="event-sessions-list">
          {sessions.map((s, i) => (
            <SessionRow
              key={s.id}
              s={s}
              i={i}
              eventTitle={event.title}
              isRegistered={!!myReg}
              isMyRsvp={mySessionRsvps.has(s.id)}
              tablesCount={Number(tablesCountBySession?.[s.id] ?? 0)}
              lobbyTo={`/lobby?event=${encodeURIComponent(event.id)}&session=${encodeURIComponent(s.id)}`}
              onToggle={() => onToggleRsvp(s.id)}
            />
          ))}
        </ol>
      </div>
    </section>
  );
});

// ---------------------------------------------------------------------------
// LOADING / ERROR STATES — tiny, module-scope
// ---------------------------------------------------------------------------

const LoadingState = () => (
  <div className="min-h-screen bg-[#061412]">
    {/* Hero skeleton */}
    <div className="container-x pt-24 md:pt-32 pb-16">
      <div className="h-4 w-24 bg-white/[0.06] rounded animate-pulse mb-8" />
      <div className="h-16 w-2/3 bg-white/[0.06] rounded-xl animate-pulse mb-4" />
      <div className="h-16 w-1/2 bg-white/[0.06] rounded-xl animate-pulse mb-8" />
      <div className="h-5 w-full max-w-2xl bg-white/[0.04] rounded animate-pulse mb-2" />
      <div className="h-5 w-4/5 max-w-xl bg-white/[0.04] rounded animate-pulse mb-10" />
      <div className="grid grid-cols-2 md:grid-cols-4 gap-8 max-w-3xl">
        {Array.from({ length: 4 }, (_, i) => (
          <div key={i} className="space-y-2">
            <div className="h-3 w-12 bg-white/[0.06] rounded animate-pulse" />
            <div className="h-4 w-24 bg-white/[0.05] rounded animate-pulse" />
          </div>
        ))}
      </div>
    </div>
  </div>
);

const NotFoundState = () => (
  <div className="min-h-screen bg-[#061412] flex items-center justify-center">
    <div className="container-x py-32 text-center">
      <p className="font-display text-3xl text-white/40 mb-4">
        Event not found
      </p>
      <Link to="/events" className="btn-ghost text-sm">
        ← Back to calendar
      </Link>
    </div>
  </div>
);

// ---------------------------------------------------------------------------
// MAIN PAGE COMPONENT
// ---------------------------------------------------------------------------

export default function EventDetail() {
  const { eventId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useAuth();

  // ---------------------------------------------------------------------------
  // Core state
  // ---------------------------------------------------------------------------
  const [event, setEvent] = useState(null); // null = loading, false = 404
  const [sessions, setSessions] = useState([]);
  const [sponsors, setSponsors] = useState([]);
  const [activeSessionId, setActiveSessionId] = useState(null);

  // Tables state — unified: both the rendered list and per-session counts
  const [tables, setTables] = useState([]);
  const [tablesCountBySession, setTablesCountBySession] = useState({});

  // Registration state
  const [myReg, setMyReg] = useState(null);
  const [mySessionRsvps, setMySessionRsvps] = useState(new Set());

  // Modal open/close flags — booleans only, no object allocations
  const [registerOpen, setRegisterOpen] = useState(false);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [preJoinOpen, setPreJoinOpen] = useState(false);
  const [activeOffer, setActiveOffer] = useState(null);

  // Abort refs — one per fetch effect so we never set state on unmounted
  const eventAbortRef = useRef(null);
  const tablesAbortRef = useRef(null);

  // ---------------------------------------------------------------------------
  // Query-string side effect: "?canceled=1" toast
  // ---------------------------------------------------------------------------
  useEffect(() => {
    if (!searchParams.get("canceled")) return;
    toast.error("Payment canceled. Your seat is held but not confirmed.");
    const next = new URLSearchParams(searchParams);
    next.delete("canceled");
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);

  // ---------------------------------------------------------------------------
  // EVENT FETCH — single, abortable, sets all derived state in one pass
  // ---------------------------------------------------------------------------
  const loadEvent = useCallback(async () => {
    // Cancel any previous in-flight request
    eventAbortRef.current?.abort();
    eventAbortRef.current = new AbortController();

    try {
      const res = await getEventById(eventId);
      if (eventAbortRef.current?.signal.aborted) return;

      if (!res?.success || !res?.data) {
        setEvent(false);
        return;
      }

      const { event, sponsors, sessions, activeSessionId, raw } =
        normalizeEventResponse(res.data);

      setEvent(event);
      setSessions(sessions);
      setSponsors(sponsors);
      setActiveSessionId(activeSessionId);

      // Registration status — derived from the same response, no extra request
      if (raw.isBooked) {
        const myId = user?._id || user?.id;
        const mine = Array.isArray(raw.bookingDetails)
          ? raw.bookingDetails.find((b) => {
              const uid = b?.userId?._id || b?.userId;
              return myId && uid && String(uid) === String(myId);
            })
          : null;

        const paid = Number(raw.ticketPrice ?? 0) <= 0;
        setMyReg({
          payment_status: paid ? "paid" : "pending",
          ref_code: mine?.ticketNumber || "",
        });
      } else {
        setMyReg(null);
      }
    } catch (err) {
      if (err?.name === "AbortError") return;
      setEvent(false);
      toast.error("Failed to load event.");
    }
  }, [eventId, user]);

  useEffect(() => {
    setEvent(null); // reset to loading state on eventId change
    loadEvent();
    return () => eventAbortRef.current?.abort();
  }, [loadEvent]);

  // ---------------------------------------------------------------------------
  // TABLES FETCH — fires when sessions or activeSessionId change
  // ---------------------------------------------------------------------------
  useEffect(() => {
    tablesAbortRef.current?.abort();
    tablesAbortRef.current = new AbortController();

    if (!sessions.length) {
      setTables([]);
      setTablesCountBySession({});
      setEvent((prev) => (prev ? { ...prev, tables_count: 0 } : prev));
      return;
    }

    let alive = true;

    const run = async () => {
      try {
        // Fetch all sessions in parallel but with allSettled so one failure
        // doesn't block the rest
        const results = await Promise.allSettled(
          sessions.map((s) => getSessionTables(s.id)),
        );

        if (!alive) return;

        // Build a sessionId → raw tables array map
        const tableMap = {};
        sessions.forEach((s, idx) => {
          const r = results[idx];
          tableMap[s.id] =
            r.status === "fulfilled" ? r.value?.data?.tables || [] : [];
        });

        // Per-session counts
        const counts = {};
        let total = 0;
        for (const [sid, arr] of Object.entries(tableMap)) {
          const n = Array.isArray(arr) ? arr.length : 0;
          counts[sid] = n;
          total += n;
        }

        setTablesCountBySession(counts);
        setEvent((prev) => (prev ? { ...prev, tables_count: total } : prev));

        // Render the active session's tables
        const chosen = activeSessionId || sessions[0]?.id;
        setTables(
          normalizeTablesFromSessionTablesApi(tableMap[chosen] || [], eventId),
        );
      } catch {
        // Silent — table section just stays empty
      }
    };

    run();
    return () => {
      alive = false;
    };
  }, [sessions, activeSessionId, eventId]);

  // ---------------------------------------------------------------------------
  // REFETCH TABLES for a single session (e.g. after joining a table)
  // ---------------------------------------------------------------------------
  const refetchTables = useCallback(async () => {
    if (!activeSessionId) return;
    try {
      const res = await getSessionTables(activeSessionId);
      const tablesArr = res?.data?.tables || [];
      setTables(normalizeTablesFromSessionTablesApi(tablesArr, eventId));
      setTablesCountBySession((prev) => ({
        ...prev,
        [activeSessionId]: tablesArr.length,
      }));
    } catch {
      // Silent
    }
  }, [activeSessionId, eventId]);

  // ---------------------------------------------------------------------------
  // STABLE HANDLERS — defined once, passed as props so children don't re-render
  // ---------------------------------------------------------------------------
  const handleRegisterOpen = useCallback(() => setRegisterOpen(true), []);
  const handleRegisterClose = useCallback(() => setRegisterOpen(false), []);
  const handleCancelOpen = useCallback(() => setCancelOpen(true), []);
  const handleCancelClose = useCallback(() => setCancelOpen(false), []);
  const handlePreJoinOpen = useCallback(() => setPreJoinOpen(true), []);
  const handlePreJoinClose = useCallback(() => setPreJoinOpen(false), []);

  const handleBooked = useCallback(
    async (ticketNumber) => {
      const paid = Number(event?.price || 0) <= 0;
      setMyReg({
        payment_status: paid ? "paid" : "pending",
        ref_code: ticketNumber || "",
      });
      await loadEvent();
    },
    [event?.price, loadEvent],
  );

  const handleCancelled = useCallback(async () => {
    setMyReg(null);
    await loadEvent();
  }, [loadEvent]);

  const handleCompletePayment = useCallback(() => {
    setMyReg((r) => (r ? { ...r, payment_status: "paid" } : r));
    toast.success("Payment received — your seat is confirmed.");
  }, []);

  const handlePreJoinReady = useCallback(() => {
    setPreJoinOpen(false);
    toast.success("You're ready — entering the room.");
  }, []);

  const handleToggleRsvp = useCallback((sessionId) => {
    setMySessionRsvps((prev) => {
      const next = new Set(prev);
      if (next.has(sessionId)) {
        next.delete(sessionId);
        toast.success("Removed from your sessions.");
      } else {
        next.add(sessionId);
        toast.success("Added to your sessions.");
      }
      return next;
    });
  }, []);

  // ---------------------------------------------------------------------------
  // DERIVED VALUES — memoised so they don't recalculate on every render
  // ---------------------------------------------------------------------------

  // isLive — stable snapshot; acceptable to recalculate on event change
  const isLive = useMemo(() => {
    if (!event?.start_time || !event?.end_time) return false;
    const now = Date.now();
    return (
      now >= new Date(event.start_time).getTime() &&
      now < new Date(event.end_time).getTime()
    );
  }, [event?.start_time, event?.end_time]);

  // Event-scoped sponsors
  const eventSponsors = useMemo(
    () => sponsors.filter((s) => s.scope === "event"),
    [sponsors],
  );

  // ICS URL — expensive encodeURIComponent, only recompute when event changes
  const eventIcsUrl = useMemo(
    () => (event ? icsDataUrlForEvent(event) : ""),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [event?.id, event?.start_time, event?.end_time, event?.title],
  );

  // ---------------------------------------------------------------------------
  // RENDER GUARDS
  // ---------------------------------------------------------------------------
  if (event === false) return <NotFoundState />;
  if (!event) return <LoadingState />;

  // ---------------------------------------------------------------------------
  // RENDER
  // ---------------------------------------------------------------------------
  return (
    <div data-testid="event-detail-page" className="min-h-screen">
      <EventHero
        event={event}
        isLive={isLive}
        myReg={myReg}
        eventSponsors={eventSponsors}
        icsUrl={eventIcsUrl}
        onRegisterOpen={handleRegisterOpen}
        onCancelOpen={handleCancelOpen}
        onPreJoinOpen={handlePreJoinOpen}
        onCompletePayment={handleCompletePayment}
      />

      <SessionsSection
        sessions={sessions}
        event={event}
        myReg={myReg}
        mySessionRsvps={mySessionRsvps}
        tablesCountBySession={tablesCountBySession}
        onToggleRsvp={handleToggleRsvp}
      />

      <WelcomeVideoOverlay
        src={event.welcome_video_url}
        title={event.title}
        subtitle="A short note from your host before the doors open."
        storageKey={`welcome:event:${event.id}`}
      />

      {/* Modals — only mount DOM when open */}
      <RegisterModal
        open={registerOpen}
        onClose={handleRegisterClose}
        event={event}
        user={user}
        onBooked={handleBooked}
      />

      <CancelRegistrationModal
        open={cancelOpen}
        onClose={handleCancelClose}
        eventId={event.id}
        onCancelled={handleCancelled}
      />

      <StaticWaitlistOfferModal
        offer={activeOffer}
        onClose={() => setActiveOffer(null)}
        onAccepted={refetchTables}
      />

      <StaticPreJoinModal
        open={preJoinOpen}
        onClose={handlePreJoinClose}
        onReady={handlePreJoinReady}
      />
    </div>
  );
}
